import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { ErrorSeverity, InformationalDialogComponent } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { CompleteProfileDialogComponent } from 'src/app/global/components/dialogs/complete-account-dialog/complete-profile-dialog.component';
import { ConsultationConnectorAccessType } from '@bciti/types';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { State } from 'src/app/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { AuthService } from '../service/auth.abstract.service';

@Injectable({ providedIn: 'root' })
export class ExternalConsultationGuard implements CanActivate {
    constructor(
        private store: Store<State>,
        private authService: AuthService,
        private dialog: MatDialog,
        private t: TranslateService,
    ) {}

    canActivate(snapshot: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> {
        return combineLatest([this.store.select(organisationConfigurationSelector), this.store.select(profileSelector)]).pipe(
            take(1),
            map(([orgConfig, user]) => {
                if (!orgConfig || !user) {
                    return false;
                }

                if (orgConfig?.consultation?.accessType === ConsultationConnectorAccessType.REDIRECT) {
                    // Authenticate user to external provider
                    this.authenticate(orgConfig.consultation.provider).subscribe();
                } else {
                    // default bciti consultation module.
                    return true;
                }
            }),
        );
    }

    private authenticate(provider: string) {
        return this.authService.loginToExternalProvider(provider).pipe(
            take(1),
            tap(async (redirectUrl: string) => {
                setTimeout(() => {
                    window.open(redirectUrl, '_blank');
                }, 100); // Timeout is a safari workaround.
            }),
            catchError((err) => {
                if (err.status === 400 && err.error?.requiredInfo?.length > 0) {
                    this.dialog.open(CompleteProfileDialogComponent, {
                        panelClass: 'complete-profile-dialog',
                        autoFocus: false,
                    });
                } else {
                    this.dialog.open(InformationalDialogComponent, {
                        data: {
                            title: this.t.instant('common.errors.somethingWentWrong'),
                            body: this.t.instant('common.errors.default'),
                            errorSeverity: ErrorSeverity.Error,
                        },
                        autoFocus: false,
                        panelClass: 'informational-dialog',
                    });
                }

                return of();
            }),
        );
    }
}
